// src/components/digital_twin/twin_engine/twinExplainer.js
// FlowSense AI explainer for one asset. Builds a brief from the data the twin actually fetched
// (ML health, alerts, maintenance, KPIs, TessaDEM terrain, well intelligence, live sensors, the
// network risk model and the active simulation) — every statement cites a real value; anything
// missing is reported as a data gap, never guessed. Pure functions, no React.
import { num } from './twinData';
import { SOURCE_LABELS } from './twinData';

const DAY = 86400000;
const pct = (p) => (p == null ? null : Math.round(p * 100));
const toMs = (d) => (/^\d{4}-\d{2}-\d{2}$/.test(String(d)) ? Date.parse(`${d}T00:00:00`) : Date.parse(d));
const fmtDate = (d) => { const t = toMs(d); return Number.isFinite(t) ? new Date(t).toLocaleDateString() : null; };
const daysSince = (d) => { const t = Date.parse(d); return Number.isFinite(t) ? Math.floor((Date.now() - t) / DAY) : null; };
const TIER_ORDER = ['stable', 'watch', 'high', 'critical'];
const worse = (a, b) => (TIER_ORDER.indexOf(b) > TIER_ORDER.indexOf(a) ? b : a);

export const TIER_META = Object.freeze({
  critical: { label: 'Critical', scheme: 'red', hex: '#E53E3E' },
  high: { label: 'High risk', scheme: 'orange', hex: '#DD6B20' },
  watch: { label: 'Watch', scheme: 'yellow', hex: '#D69E2E' },
  stable: { label: 'Stable', scheme: 'green', hex: '#38A169' },
  unknown: { label: 'Not scored', scheme: 'gray', hex: '#718096' },
});

/** Live sensor rows: register sensor + latest reading (page stream) + history (backend). */
export function sensorRows(sensors = [], readings = {}, history = null) {
  return sensors.map((s) => {
    const key = String(s.device_id ?? s.id);
    const r = readings?.[key] || null;
    const hist = (history?.[key] || []).map((h) => ({
      ts: Date.parse(h.timestamp ?? h.received_at ?? h.created_at),
      value: num(h.psi ?? h.pressure ?? h.value ?? h.level_percentage ?? h.level),
    })).filter((p) => Number.isFinite(p.ts) && p.value !== null).sort((a, b) => a.ts - b.ts);
    const raw = r ? (r.psi ?? r.value ?? r.level) : null;
    const value = num(raw);
    const label = value === null && raw != null ? String(raw) : null;           // e.g. "HIGH" level bands
    const at = r?.timestamp ?? s.last_activity_at ?? s.last_seen ?? null;
    const ageMin = at ? Math.round((Date.now() - Date.parse(at)) / 60000) : null;
    const min = num(s.min_threshold); const max = num(s.max_threshold);
    let state = 'ok';
    if (r?.state && r.state !== 'ok' && r.state !== 'normal') state = r.state;
    else if (r?.status && !['normal', 'ok'].includes(String(r.status).toLowerCase())) state = String(r.status).toLowerCase();
    else if (value !== null && ((max !== null && value > max) || (min !== null && value < min))) state = 'critical';
    else if (label && /high|overflow|full/i.test(label)) state = 'warning';
    const online = s.online != null ? !!s.online : ageMin !== null ? ageMin < 120 : null;
    return {
      key, name: s.name || key, type: s.type || 'sensor', unit: r?.unit || s.unit || (s.type === 'water_level' ? '%' : s.type === 'pressure' ? 'psi' : ''),
      value, label, at, ageMin, online, state, min, max, history: hist.length ? hist : null,
    };
  });
}

/**
 * The brief. `ctx` = { asset, profile, sources, health, sensors (rows), simulationRecord, simulationMeta,
 *   downstreamCount, risk (twinIntel row), historical }
 */
export function buildBrief(ctx) {
  const { asset, profile, sources = {}, health, sensors = [], simulationRecord: sim, simulationMeta, risk, historical } = ctx;
  const name = asset?.name || `Asset ${asset?.id}`;
  const drivers = []; const actions = []; const gaps = []; const facts = [];
  let tier = null;
  const driver = (level, label, detail) => { drivers.push({ level, label, detail }); tier = worse(tier || 'stable', level === 'critical' ? 'critical' : level === 'high' ? 'high' : level === 'watch' ? 'watch' : 'stable'); };
  const action = (priority, text) => { if (!actions.some((a) => a.text === text)) actions.push({ priority, text }); };
  const well = sources.wellIntel?.status === 'ok' ? sources.wellIntel.data : null;

  // ── ML health ────────────────────────────────────────────────────────
  if (health?.score != null) {
    facts.push({ label: 'ML health', value: `${Math.round(health.score)}/100` });
    if (health.score < 40) driver('critical', 'ML health is critical', `Health score ${Math.round(health.score)}/100${health.trend ? `, trend ${health.trend}` : ''}.`);
    else if (health.score < 60) driver('high', 'ML health is degraded', `Health score ${Math.round(health.score)}/100.`);
    else if (health.score < 80) driver('watch', 'ML health is fair', `Health score ${Math.round(health.score)}/100.`);
  }
  if (health?.failureProbability != null) {
    const p = pct(health.failureProbability);
    facts.push({ label: 'Failure probability', value: `${p}%` });
    if (p >= 60) { driver('high', 'High failure probability', `The ML model gives ${p}% probability of failure.`); action('high', 'Raise a preventive work order before the predicted failure window.'); }
    else if (p >= 30) driver('watch', 'Elevated failure probability', `${p}% probability of failure.`);
  }
  if (health?.rulDays != null) {
    facts.push({ label: 'Remaining useful life', value: `${Math.round(health.rulDays)} days` });
    if (health.rulDays < 30) { driver('high', 'Short remaining life', `Predicted remaining useful life is ${Math.round(health.rulDays)} days.`); action('high', 'Plan replacement / major repair within the remaining-life window.'); }
  }
  (health?.drivers || []).slice(0, 3).forEach((d) => {
    const label = typeof d === 'string' ? d : d.label || d.feature || d.name;
    if (label) drivers.push({ level: 'info', label: `ML factor: ${label}`, detail: d.weight != null ? `weight ${d.weight}` : '' });
  });

  // ── Alerts ───────────────────────────────────────────────────────────
  const alerts = sources.alerts?.data || [];
  if (alerts.length) {
    const crit = alerts.filter((a) => a.severity === 'critical').length;
    facts.push({ label: 'Open alerts', value: `${alerts.length}${crit ? ` (${crit} critical)` : ''}` });
    driver(crit ? 'critical' : 'watch', `${alerts.length} open alert${alerts.length > 1 ? 's' : ''}`, alerts.slice(0, 2).map((a) => a.message).join(' · '));
    if (alerts.some((a) => !a.workOrderId)) action(crit ? 'urgent' : 'medium', 'Acknowledge the open alerts and link a work order to each.');
  }

  // ── Well intelligence (wells only) ───────────────────────────────────
  if (well) {
    const i = well.intel || {};
    if (i.score != null) facts.push({ label: 'Well risk score', value: `${i.score}/100 (${i.tier})` });
    if (i.tier && TIER_ORDER.includes(i.tier)) tier = worse(tier || 'stable', i.tier);
    (i.drivers || []).slice(0, 4).forEach((d) => drivers.push({ level: d.points >= 20 ? 'high' : 'watch', label: d.label, detail: d.evidence || `${d.points} pts` }));
    (i.recommendations || []).forEach((r) => action(r.priority || 'medium', r.text));
    if (well.flood?.probability != null) {
      const p = pct(well.flood.probability > 1 ? well.flood.probability / 100 : well.flood.probability);
      facts.push({ label: 'Flood probability', value: `${p}%${well.flood.est_minutes_to_overtopping != null ? ` · overtop in ~${Math.round(well.flood.est_minutes_to_overtopping)} min` : ''}` });
    }
    const ins = well.inspection || {};
    if (ins.status) facts.push({ label: 'Inspection', value: ins.status.replace(/_/g, ' ') + (ins.last_inspected_date ? ` · last ${fmtDate(ins.last_inspected_date)}` : '') });
    const near = well.drainage?.nearby || [];
    if (near.length) {
      const impaired = near.filter((d) => d.impaired).length;
      facts.push({ label: 'Linked drainage', value: `${near.length} within ${well.drainage.radius_m} m${impaired ? ` · ${impaired} impaired` : ''}` });
    }
    (i.data_gaps || []).forEach((g) => gaps.push(typeof g === 'string' ? g : g.text || g.label));
  }

  // ── Terrain (TessaDEM) ───────────────────────────────────────────────
  const elev = sources.elevation?.status === 'ok' ? sources.elevation.data : null;
  const g = num(elev?.ground_elevation_m ?? asset?.ground_elevation_m);
  if (g !== null) {
    facts.push({ label: 'Ground elevation', value: `${g.toFixed(2)} m ASL` });
    const t = elev?.terrain || null;
    const dep = num(t?.depression_m); const inflow = num(t?.inflow_fraction);
    if (profile.flood && g <= 2) driver('watch', 'Low-lying ground', `Ground is ${g.toFixed(2)} m above sea level — exposed to ponding and storm surge.`);
    if (profile.flood && dep !== null && dep > 0.25) {
      driver(dep > 1 ? 'high' : 'watch', 'Sits in a terrain low', `${dep.toFixed(2)} m below its surroundings${inflow !== null ? `; ${Math.round(inflow * 100)}% of directions drain toward it` : ''}.`);
      if (profile.family === 'drainage') action('medium', 'Prioritise this asset for desilting before the rainy season — run-off concentrates here.');
    }
    const prof = elev?.profile;
    if (prof?.grade_pct != null) facts.push({ label: 'Grade', value: `${prof.grade_pct}% over ${Math.round(prof.length_m)} m` });
    if (prof?.low_points?.length && profile.flood) drivers.push({ level: 'info', label: `${prof.low_points.length} low point(s) along the line`, detail: `lowest ${prof.low_points[0].elevation_m} m at chainage ${prof.low_points[0].chainage_m} m` });
  } else if (asset?.latitude != null) gaps.push('No TessaDEM ground elevation yet — run the elevation backfill.');

  // ── Maintenance ──────────────────────────────────────────────────────
  const maint = sources.maintenance?.data || [];
  if (maint.length) {
    const last = maint.find((m) => !m.status || /complete|done|closed/i.test(m.status)) || maint[0];
    const d = daysSince(last.date);
    facts.push({ label: 'Last maintenance', value: `${last.type}${d != null ? ` · ${d} days ago` : ''}` });
    if (d != null && d > 365) { driver('watch', 'No maintenance in over a year', `Last record: ${fmtDate(last.date)}.`); action('medium', 'Schedule routine maintenance — none recorded in the last 12 months.'); }
  } else if (sources.maintenance?.status) gaps.push('No maintenance history recorded for this asset.');

  // ── KPIs ─────────────────────────────────────────────────────────────
  const k = sources.kpis?.data;
  if (k?.availability != null) facts.push({ label: 'Availability', value: `${k.availability}%` });
  if (k?.mtbf != null) facts.push({ label: 'MTBF', value: `${k.mtbf} h` });

  // ── Sensors ──────────────────────────────────────────────────────────
  if (!sensors.length) {
    gaps.push('No sensor linked — live condition is not monitored.');
    if (['drainage_well', 'water_well', 'tank', 'pump', 'pipeline'].includes(profile.key)) action('low', `Link a ${profile.key === 'pipeline' || profile.key === 'pump' ? 'pressure' : 'water-level'} sensor so FlowSense can monitor this asset live.`);
  } else {
    const off = sensors.filter((s) => s.online === false);
    const bad = sensors.filter((s) => s.state === 'critical' || s.state === 'warning');
    facts.push({ label: 'Sensors', value: `${sensors.length} linked · ${sensors.length - off.length} online` });
    if (off.length) { driver('watch', `${off.length} sensor${off.length > 1 ? 's' : ''} offline`, off.map((s) => s.name).join(', ')); action('medium', 'Check power, antenna and gateway for the offline sensor(s).'); }
    bad.forEach((s) => driver(s.state === 'critical' ? 'critical' : 'watch', `${s.name} out of range`, `${s.value ?? s.label}${s.unit ? ` ${s.unit}` : ''}${s.max != null ? ` (limit ${s.max})` : ''}`));
  }

  // ── Network risk model (twinIntel) ───────────────────────────────────
  if (risk?.score != null) {
    facts.push({ label: 'Network risk', value: `${risk.score}/100${risk.band ? ` (${risk.band})` : ''}` });
    (risk.drivers || []).slice(0, 2).forEach((d) => drivers.push({ level: 'info', label: `Network: ${typeof d === 'string' ? d : d.label}`, detail: '' }));
    (risk.actions || []).slice(0, 2).forEach((a) => action('medium', typeof a === 'string' ? a : a.text));
  }

  // ── Simulation ───────────────────────────────────────────────────────
  if (sim && simulationMeta) {
    const s = String(sim.state || 'ok');
    if (s !== 'ok') driver(s === 'critical' || s === 'failed' ? 'high' : 'watch', `Impacted in "${simulationMeta.title}"`,
      [sim.peak_flood_depth_m != null && `peak flood ${sim.peak_flood_depth_m} m`, sim.time_to_flood_hours != null && `floods from +${sim.time_to_flood_hours} h`,
        sim.damage_state && `damage ${sim.damage_state}`].filter(Boolean).join(' · ') || `simulated state ${s}`);
    (sim.recommended_actions || []).slice(0, 2).forEach((a) => action('medium', a));
  }

  // ── Source gaps ──────────────────────────────────────────────────────
  Object.entries(sources).forEach(([key, src]) => { if (src?.status === 'error') gaps.push(`${SOURCE_LABELS[key] || key} unavailable${src.error ? `: ${src.error}` : ''}.`); });
  if (health?.score == null && sources.health?.status !== 'error') gaps.push('The ML health model has not scored this asset yet.');

  const finalTier = tier || (facts.length > 1 ? 'stable' : 'unknown');
  const top = drivers.filter((d) => d.level !== 'info').slice(0, 2).map((d) => d.label.toLowerCase());
  const headline = historical
    ? `Replay — ${name} as recorded at ${new Date(historical.at || Date.now()).toLocaleString()}.`
    : well?.intel?.summary
      ? well.intel.summary
      : finalTier === 'unknown' ? `${name} has too little data for FlowSense to assess.`
        : !top.length ? `${name} is stable — no risk drivers in the current data.`
          : `${name} is ${finalTier === 'stable' ? 'stable' : TIER_META[finalTier].label.toLowerCase()}: ${top.join(' and ')}.`;
  const P = { urgent: 0, high: 1, medium: 2, low: 3 };
  actions.sort((a, b) => (P[a.priority] ?? 9) - (P[b.priority] ?? 9));
  const covered = Object.values(sources).filter((s) => s?.status === 'ok').length;
  const asked = Object.values(sources).filter((s) => s && s.status !== 'skipped').length;
  return { name, tier: finalTier, headline, drivers, actions, gaps: [...new Set(gaps)], facts, coverage: asked ? Math.round((covered / asked) * 100) : 0 };
}

/** Compact, factual context for the FlowSense AI service. */
export function buildAiContext(ctx, brief) {
  const { asset, profile, sources = {}, sensors = [], simulationRecord, simulationMeta, isLive, historicalTimestamp } = ctx;
  const well = sources.wellIntel?.data;
  return {
    screen: 'digital_twin_asset',
    mode: isLive ? 'live' : `replay ${historicalTimestamp ? new Date(historicalTimestamp).toISOString() : ''}`,
    asset: {
      id: asset?.id, name: asset?.name, register_type: asset?.asset_type || asset?.type, kind: profile.label, family: profile.family,
      status: asset?.status, criticality: asset?.asset_criticality, location: asset?.location_description,
      latitude: asset?.latitude, longitude: asset?.longitude,
    },
    brief: { tier: brief.tier, headline: brief.headline, facts: brief.facts, drivers: brief.drivers.slice(0, 10), actions: brief.actions.slice(0, 8), data_gaps: brief.gaps },
    ml_health: ctx.health || null,
    health_history_points: sources.history?.data?.length || 0,
    kpis: sources.kpis?.data || null,
    alerts: (sources.alerts?.data || []).slice(0, 10),
    maintenance: (sources.maintenance?.data || []).slice(0, 8),
    elevation: sources.elevation?.data ? { ground_m: sources.elevation.data.ground_elevation_m, surveyed_m: sources.elevation.data.surveyed_elevation_m, terrain: sources.elevation.data.terrain, grade_pct: sources.elevation.data.profile?.grade_pct } : null,
    well_intel: well ? { intel: well.intel, inspection: well.inspection, flood: well.flood, drainage: (well.drainage?.nearby || []).map((d) => ({ name: d.name, role: d.role, distance_m: d.distance_m, impaired: d.impaired, reason: d.reason, inspection: d.inspection?.status, elevation_drop_m: d.elevation_drop_m })) } : null,
    sensors: sensors.map((s) => ({ name: s.name, type: s.type, value: s.value ?? s.label, unit: s.unit, online: s.online, state: s.state, age_min: s.ageMin })),
    simulation: simulationRecord && simulationMeta ? { scenario: simulationMeta.title, record: simulationRecord } : null,
  };
}

export const ASSET_QUESTIONS = Object.freeze([
  { id: 'why', q: 'Why does it have this rating?' },
  { id: 'do', q: 'What should the crew do next?' },
  { id: 'flood', q: 'Is this asset at flood risk?' },
  { id: 'maint', q: 'What is its maintenance history?' },
  { id: 'gaps', q: 'What data is missing?' },
]);

/** Rule-based answers from the brief — used offline and as the instant answer. */
export function answerLocally(id, brief, ctx) {
  const list = (xs) => xs.map((x) => `• ${x}`).join('\n');
  switch (id) {
    case 'why': return brief.drivers.length ? `${brief.headline}\n\n${list(brief.drivers.map((d) => `${d.label}${d.detail ? ` — ${d.detail}` : ''}`))}` : `${brief.headline}\n\nNo risk drivers were found in the data FlowSense holds for this asset.`;
    case 'do': return brief.actions.length ? list(brief.actions.map((a) => `[${a.priority}] ${a.text}`)) : 'No action is needed on the current data. Keep the inspection cycle.';
    case 'flood': {
      const f = brief.facts.filter((x) => /elevation|flood|drainage|grade/i.test(x.label));
      const d = brief.drivers.filter((x) => /low|flood|terrain|run-off|ponding|drain/i.test(`${x.label} ${x.detail}`));
      if (!ctx.profile.flood) return `${ctx.profile.label}s are not part of the flood model. ${f.length ? list(f.map((x) => `${x.label}: ${x.value}`)) : ''}`;
      return f.length || d.length ? list([...f.map((x) => `${x.label}: ${x.value}`), ...d.map((x) => `${x.label} — ${x.detail}`)]) : 'No terrain or flood data is available for this asset yet.';
    }
    case 'maint': {
      const m = ctx.sources?.maintenance?.data || [];
      return m.length ? list(m.slice(0, 6).map((x) => `${fmtDate(x.date) || 'undated'} — ${x.type}${x.status ? ` (${x.status})` : ''}${x.description ? `: ${x.description}` : ''}`)) : 'No maintenance has been recorded for this asset in FlowSense.';
    }
    case 'gaps': return brief.gaps.length ? list(brief.gaps) : 'Every data source for this asset responded.';
    default: return brief.headline;
  }
}
